import React from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import RudrasLogo from "./RudrasLogo";

/**
 * BrandCinematic — premium right-side visual for /login and /register.
 *
 * Design intent (Feb 2026, Phase 3 / P4):
 *  - Live React composition (NOT a static PNG) so it scales crisply on every viewport.
 *  - Dark emerald cinematic palette matching the rest of the Rudras-AI brand
 *    (Showcase Dashboard, Cinematic Walkthrough end-card, Public Chatbot header).
 *  - Headline tagline: "Your AI Employee — Built on the Intelligence Layer for Modern Businesses."
 *  - Subtle motion: floating glow orbs (CSS keyframe), pulsing "live" dots inside
 *    a mock assistant card, a slow grain overlay for depth — no JS animations,
 *    no images, no external deps.
 *  - Mobile: collapses to a slim emerald brand strip with the logo + product
 *    name; the full panel is hidden on viewports < md.
 *
 * Props
 *  - variant: "login" | "register" — flips the small "trust strip" copy.
 *  - className: extra wrapper classes (e.g. order toggle for register layout).
 *  - "data-testid" passed through for E2E selectors.
 */
export default function BrandCinematic({ variant = "login", className = "", ...rest }) {
  const isLogin = variant === "login";
  return (
    <>
      {/* Mobile: slim emerald brand strip — visible only when the main panel is hidden. */}
      <div
        className="md:hidden flex items-center justify-between px-5 py-3 bg-emerald-950 text-emerald-50"
        data-testid="brand-strip-mobile"
      >
        <Link to="/" className="flex items-center gap-2 group">
          <RudrasLogo size="md" ringed className="ring-amber-300/30" />
          <span className="font-display font-semibold tracking-tight">Rudras-AI</span>
        </Link>
        <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300/80">
          AI Employee
        </span>
      </div>

      {/* Desktop: full cinematic panel */}
      <aside
        className={`relative hidden md:flex flex-col justify-between overflow-hidden bg-emerald-950 text-emerald-50 p-10 lg:p-14 ${className}`}
        data-testid="brand-cinematic-panel"
        {...rest}
      >
        {/* layered background — radial glows + grain */}
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -left-20 w-[420px] h-[420px] rounded-full bg-emerald-600/25 blur-[110px] brand-orb-a" />
          <div className="absolute top-1/3 -right-32 w-[460px] h-[460px] rounded-full bg-amber-400/12 blur-[120px] brand-orb-b" />
          <div className="absolute bottom-0 left-1/3 w-[300px] h-[300px] rounded-full bg-emerald-400/15 blur-[90px] brand-orb-c" />
          <div className="absolute inset-0 brand-grain" />
        </div>

        {/* Top brand cluster */}
        <div className="relative">
          <Link to="/" className="inline-flex items-center gap-2.5 group" data-testid="brand-cinematic-home">
            <RudrasLogo size="lg" ringed className="ring-amber-300/30" />
            <span className="font-display font-semibold text-xl tracking-tight">Rudras-AI</span>
          </Link>
          <div className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-emerald-300/80 px-3 py-1 rounded-full ring-1 ring-emerald-400/20 bg-emerald-900/40 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-300 animate-pulse" />
            Intelligence Layer · Live
          </div>
        </div>

        {/* Middle — tagline */}
        <div className="relative z-10 max-w-md">
          <h2 className="font-display font-semibold leading-[1.05] text-3xl lg:text-[2.6rem] text-emerald-50">
            Your AI Employee
            <span className="block mt-2 text-amber-300/95 font-display">
              built on the Intelligence Layer for modern businesses.
            </span>
          </h2>
          <p className="mt-5 text-emerald-100/70 text-sm leading-relaxed max-w-sm">
            A 24/7 sales engineer that listens, qualifies, and quotes — trained on
            your own product, pricing, and ICP. Same shift, every shift.
          </p>

          {/* Mock assistant card — feels like a peek into the product */}
          <div className="mt-7 rounded-2xl border border-emerald-400/15 bg-emerald-900/40 backdrop-blur-sm p-4 shadow-2xl shadow-emerald-950/40">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-7 h-7 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center ring-1 ring-amber-300/40">
                <Sparkles className="w-3.5 h-3.5 text-amber-200" />
              </span>
              <div className="leading-tight">
                <div className="text-[12.5px] font-semibold tracking-tight text-emerald-50">Sara · Sales Engineer</div>
                <div className="text-[10.5px] text-emerald-300/70 uppercase tracking-wider">Healthcare assistant · powered by Rudras-AI</div>
              </div>
              <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-emerald-200/80">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse" />
                live
              </span>
            </div>
            <div className="space-y-2">
              <div className="rounded-lg bg-emerald-950/60 border border-emerald-700/30 px-3 py-2 text-[12.5px] text-emerald-100/90">
                "Can the EMR integrate with our lab system?"
              </div>
              <div className="rounded-lg bg-amber-300/10 border border-amber-300/20 px-3 py-2 text-[12.5px] text-amber-100/95 leading-snug">
                Yes — we ship native HL7 + FHIR connectors for major labs. For your
                practice size I'd recommend the Group tier; want a quick ROI estimate?
              </div>
            </div>
          </div>
        </div>

        {/* Bottom — trust strip + footer */}
        <div className="relative z-10 space-y-4">
          <div className="grid grid-cols-3 gap-2 text-[10.5px] uppercase tracking-[0.18em] text-emerald-300/70">
            <div className="border-l border-emerald-400/20 pl-3">Discovery first</div>
            <div className="border-l border-emerald-400/20 pl-3">Always on-brand</div>
            <div className="border-l border-emerald-400/20 pl-3">Quote with reason</div>
          </div>
          <div className="pt-3 border-t border-emerald-400/15 text-[11px] text-emerald-300/60 flex items-center justify-between">
            <span>© 2026 Rudras-AI</span>
            <span className="font-mono tracking-tight">
              {isLogin ? "/ sign-in" : "/ create assistant"}
            </span>
          </div>
        </div>
      </aside>

      <style>{`
        @keyframes brand-orb-float-a { 0%,100%{transform:translate(0,0)} 50%{transform:translate(20px,-15px)} }
        @keyframes brand-orb-float-b { 0%,100%{transform:translate(0,0)} 50%{transform:translate(-25px,18px)} }
        @keyframes brand-orb-float-c { 0%,100%{transform:translate(0,0)} 50%{transform:translate(12px,-10px)} }
        .brand-orb-a { animation: brand-orb-float-a 18s ease-in-out infinite; }
        .brand-orb-b { animation: brand-orb-float-b 22s ease-in-out infinite; }
        .brand-orb-c { animation: brand-orb-float-c 16s ease-in-out infinite; }
        .brand-grain {
          background-image: radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px);
          background-size: 3px 3px;
          opacity: 0.5;
          mix-blend-mode: overlay;
        }
        @media (prefers-reduced-motion: reduce) {
          .brand-orb-a, .brand-orb-b, .brand-orb-c { animation: none; }
        }
      `}</style>
    </>
  );
}
